import { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ShieldCheck, ExternalLink,
  LayoutDashboard, Building2, CreditCard, Sparkles, Tag, Store,
  Activity, ScrollText, Wand2, ShieldAlert, Compass,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { MobileNavDrawer } from '@/components/shell/MobileNavDrawer';
import { WorkspaceSwitcher, SandboxBadge } from '@/components/admin/WorkspaceSwitcher';
import { useAuth } from '@/lib/auth';

const LABELS = {
  admin: 'Operations',
  banks: 'Banks',
  cards: 'Cards',
  rules: 'Reward Rules',
  'audit-log': 'Audit Log',
  mccs: 'MCCs',
  offers: 'Offers',
  merchants: 'Merchants',
  ingest: 'Ingest card',
  'ingestion-jobs': 'Ingestion jobs',
  qa: 'Rule QA',
  'routing-hints': 'Routing hints',
  'routing-suggestions': 'Suggested hints',
};

// Mirror of AdminSidebar groups so the mobile drawer has the same coverage.
const ADMIN_NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, testid: 'admin-nav-dashboard' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { to: '/admin/banks', label: 'Banks', icon: Building2, testid: 'admin-nav-banks' },
      { to: '/admin/cards', label: 'Cards', icon: CreditCard, testid: 'admin-nav-cards' },
      { to: '/admin/ingest', label: 'Ingest card', icon: Wand2, testid: 'admin-nav-ingest' },
      { to: '/admin/ingestion-jobs', label: 'Ingestion jobs', icon: Activity, testid: 'admin-nav-ingestion-jobs' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { to: '/admin/routing-hints', label: 'Routing hints', icon: Compass, testid: 'admin-nav-routing-hints' },
      { to: '/admin/mccs', label: 'MCCs', icon: Tag, disabled: true, soon: true, testid: 'admin-nav-mccs' },
      { to: '/admin/offers', label: 'Offers', icon: Sparkles, disabled: true, soon: true, testid: 'admin-nav-offers' },
      { to: '/admin/merchants', label: 'Merchants', icon: Store, disabled: true, soon: true, testid: 'admin-nav-merchants' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/admin/qa', label: 'Rule QA', icon: ShieldAlert, testid: 'admin-nav-qa' },
      { to: '/admin/audit-log', label: 'Audit Log', icon: ScrollText, testid: 'admin-nav-audit-log' },
    ],
  },
  {
    items: [
      { to: '/dashboard', label: 'Exit to consumer app', icon: ExternalLink, testid: 'admin-nav-exit' },
    ],
  },
];

const buildCrumbs = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  const crumbs = [];
  let acc = '';
  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i];
    acc += `/${seg}`;
    const isId = /^[a-z]+-[a-z0-9-]+$/i.test(seg);
    crumbs.push({
      href: acc,
      label: isId ? seg : (LABELS[seg] || seg),
      isId,
    });
  }
  return crumbs;
};

export const AdminTopBar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const crumbs = buildCrumbs(location.pathname);
  const lastCrumb = crumbs[crumbs.length - 1];

  return (
    <header
      data-testid="admin-shell-topbar"
      className="sticky top-0 z-40 border-b border-border bg-[hsl(var(--background))]/85 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--background))]/70"
    >
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 lg:px-6 h-12">
        {/* Mobile hamburger */}
        <MobileNavDrawer
          groups={ADMIN_NAV_GROUPS}
          title="Operations"
          subtitle="Admin · CardAssist Online"
          accent="admin"
          testid="admin-mobile-nav"
        />

        <Badge
          variant="outline"
          className="rounded-full bg-accent/10 text-accent border-accent/30 gap-1.5 px-2.5 shrink-0"
          data-testid="admin-shell-admin-badge"
        >
          <ShieldCheck className="h-3 w-3" />
          <span className="text-[11px] font-medium tracking-wide">Admin</span>
        </Badge>

        {/* Active workspace pill — only renders when sandbox is selected
            (non-locked admins). Demo admins see the locked badge below. */}
        <SandboxBadge />

        {/* Breadcrumbs visible on md+ */}
        <Breadcrumb data-testid="admin-shell-breadcrumb" className="hidden md:flex min-w-0">
          <BreadcrumbList>
            {crumbs.map((c, i) => {
              const isLast = i === crumbs.length - 1;
              // IMPORTANT: BreadcrumbItem and BreadcrumbSeparator each render
              // as <li>. Nesting BreadcrumbSeparator inside BreadcrumbItem
              // produces invalid <li><li> nesting (React hydration warning).
              // Render the separator as a SIBLING of the item using a
              // React.Fragment with a stable key.
              return (
                <Fragment key={c.href}>
                  <BreadcrumbItem>
                    {isLast ? (
                      <BreadcrumbPage className={c.isId ? 'font-mono-num text-[11px]' : 'text-xs'}>
                        {c.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={c.href} className="text-xs text-muted-foreground hover:text-foreground">
                          {c.label}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {!isLast && <BreadcrumbSeparator />}
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>

        {/* Compact current-page label on mobile (single value, no chain) */}
        {lastCrumb && (
          <div className="md:hidden truncate text-xs font-medium" data-testid="admin-shell-mobile-page-label">
            {lastCrumb.label}
          </div>
        )}

        <div className="flex-1" />

        {/* Desktop: full text button to switch back to consumer */}
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="hidden sm:inline-flex h-8 px-2.5 gap-1.5 text-muted-foreground hover:text-foreground"
          data-testid="admin-shell-open-consumer"
        >
          <Link to="/dashboard">
            <ExternalLink className="h-3.5 w-3.5" /> Consumer app
          </Link>
        </Button>
        {/* Mobile: compact icon-only */}
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="sm:hidden h-8 w-8 text-muted-foreground hover:text-foreground"
          data-testid="admin-shell-open-consumer-mobile"
          aria-label="Switch to consumer app"
        >
          <Link to="/dashboard">
            <ExternalLink className="h-[18px] w-[18px]" />
          </Link>
        </Button>

        <ThemeToggle />

        {/* Workspace switcher — dropdown for regular admins, locked badge for demo. */}
        <WorkspaceSwitcher />

        <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-border">
          <div className="h-7 w-7 rounded-full bg-primary/10 border border-primary/20 grid place-items-center text-[11px] font-semibold text-primary">
            {(user?.name || 'A').split(' ').map((p) => p[0]).slice(0, 2).join('')}
          </div>
          <div className="leading-tight hidden lg:block">
            <div className="text-xs font-medium">{user?.name?.split(' ')[0] || 'Admin'}</div>
            <div className="text-[10px] text-muted-foreground">{user?.email}</div>
          </div>
        </div>
      </div>
    </header>
  );
};
